import {
  Directive,
  EventEmitter,
  Output,
  OnInit,
  ElementRef,
  OnDestroy,
  Input,
} from '@angular/core';

import { gmapsLoadPromise } from '../lib/google-maps';
import '!!style-loader!css-loader!sass-loader!./search-places.directive.scss';
import { KEY_CODES } from '../lib/key-codes';
import { observeElement, createKeyboardEvent } from '../lib/utils';
import {
  convertDMStoDD,
  convertDMMtoDD,
  LATLNG_DD_FORMAT,
  LATLNG_DMM_FORMAT,
  LATLNG_DMS_FORMAT,
} from './latlng-utils';

const NO_LOCATION_FOUND_ERROR_TEXT: string = $localize`No location found`;

@Directive({
  selector: '.google-places-search',
})
export class SearchPlacesDirective implements OnInit, OnDestroy {
  @Output() public onPlaceChange: EventEmitter<any> = new EventEmitter();
  @Input() public autoCompleteOptions: google.maps.places.AutocompleteOptions = {};
  public searchPlaceEl: google.maps.places.Autocomplete;
  private element: HTMLInputElement;
  private place: google.maps.places.PlaceResult;
  private onPlaceChanged: google.maps.MapsEventListener;
  private noLocationFoundEl: HTMLElement;
  private mutationObserver: MutationObserver;
  private pacContainer: HTMLElement;
  private isPlaceSelecting: boolean = false;
  private isLatLngEntered: boolean = false;
  private geocoderService: google.maps.Geocoder;
  private latLngFormat: string;
  private isErrorOnLastBlur: boolean = false;

  constructor(elementRef: ElementRef) {
    this.element = elementRef.nativeElement;
  }

  public ngOnInit(): void {
    gmapsLoadPromise.then(gmaps => {
      this.geocoderService = new google.maps.Geocoder();
      this.initPlacesSearch();
    });
  }

  public ngOnDestroy(): void {
    if (this.mutationObserver) {
      this.mutationObserver.disconnect();
      this.mutationObserver = null;
    }
  }

  public startObserving(): void {
    this.mutationObserver.observe(this.pacContainer, { childList: true });
  }

  public stopObserving(): void {
    this.mutationObserver.disconnect();
  }

  private get isNotFoundErrorVisible(): boolean {
    return document.querySelector('body > .searched-loc-not-found') && this.noLocationFoundEl.style.display != 'none';
  }

  private get isPlaceSelected(): boolean {
    if (!this.place) return false;
    else if (this.place.formatted_address) return true;
  }

  private initNoLocationFoundError(): void {
    this.noLocationFoundEl = document.createElement('div');
    this.noLocationFoundEl.classList.add('searched-loc-not-found');
    this.noLocationFoundEl.innerHTML = `<span>${NO_LOCATION_FOUND_ERROR_TEXT}</span>`;
    document.body.appendChild(this.noLocationFoundEl);
  }

  private initPlacesSearch(): void {
    this.handleTabPress(this.element);

    this.element.addEventListener('focus', this.initializeGooglePlaceSearch.bind(this));
    this.element.addEventListener('blur', this.removeGooglePlaceSearch.bind(this));
    this.element.addEventListener('input', this.onInputChanged.bind(this));
    this.element.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  private initializeGooglePlaceSearch(): void {
    this.initNoLocationFoundError();
    this.initMutationObserver();
    this.detectDropdownExistance();

    // Remove old listeners which are now invalid because we removed dom elments on blur.
    if (this.onPlaceChanged) google.maps.event.removeListener(this.onPlaceChanged);
    if (this.searchPlaceEl) google.maps.event.clearInstanceListeners(this.searchPlaceEl);

    this.searchPlaceEl = new google.maps.places.Autocomplete(this.element, this.autoCompleteOptions);
    this.onPlaceChanged = this.searchPlaceEl.addListener('place_changed', () => {
      this.place = this.searchPlaceEl.getPlace();
      this.isPlaceSelecting = false;
      this.onPlaceChange.emit(this.place);

      if (this.place.formatted_address) {
        this.hideNoLocErrorDiv();
        this.element.blur();
      }
    });

    if (this.isErrorOnLastBlur && this.element.value.length) { // get error info from last blur
      this.isErrorOnLastBlur = false;
      this.showNoLocErrorDiv();
    }
  }

  private removeGooglePlaceSearch(): void {
    if (this.isNotFoundErrorVisible) this.place = null;

    this.isErrorOnLastBlur = !this.pacContainer.childElementCount;

    // Used parentNode.removeChild() because IE does not support el.remove();
    if (this.pacContainer) this.pacContainer.parentNode.removeChild(this.pacContainer);

    const noLocFoundError: HTMLElement = document.querySelector('.searched-loc-not-found') as HTMLElement;
    // Used parentNode.removeChild() because IE does not support el.remove();
    if (noLocFoundError) noLocFoundError.parentNode.removeChild(noLocFoundError);

    if (this.mutationObserver) this.mutationObserver.disconnect();
    this.mutationObserver = null;
  }

  private onInputChanged(): void {
    this.isLatLngEntered = this.checkLatLngFormat(this.element.value);
    if (this.isLatLngEntered || this.element.value.length === 0) {
      this.hideNoLocErrorDiv();
      return;
    }

    this.place = null;
    this.isPlaceSelecting = false;
    if (this.isNotFoundErrorVisible) this.hideNoLocErrorState();
  }

  private setDropdownBounds(): void {
    if (this.pacContainer && this.pacContainer.hasChildNodes()) {
      const inputBoundingRect: ClientRect = this.element.getBoundingClientRect();
      this.pacContainer.style.top = `${inputBoundingRect.bottom}px`;
      this.pacContainer.style.left = `${inputBoundingRect.left}px`;
      this.pacContainer.style.width = `${inputBoundingRect.width}px`;
      this.pacContainer.style.display = 'block';
    }
  }

  private onKeyDown(event: KeyboardEvent): void {
    const key: number = event.which || event.keyCode;
    if (key === KEY_CODES.Enter) {
      if (!this.pacContainer.hasChildNodes() && this.isNotFoundErrorVisible && !this.isLatLngEntered) this.showNoLocErrorState();
    }
  }

  private checkLatLngFormat(input: string): boolean {
    if (LATLNG_DD_FORMAT.test(input)) {
      this.latLngFormat = 'dd';
      return true;
    } else if (LATLNG_DMS_FORMAT.test(input)) {
      this.latLngFormat = 'dms';
      return true;
    } else if (LATLNG_DMM_FORMAT.test(input)) {
      this.latLngFormat = 'dmm';
      return true;
    }

    this.latLngFormat = '';
    return false;
  }

  private convertTextToLatLng(input: string): google.maps.LatLng {
    let lat: number;
    let lng: number;
    if (this.latLngFormat === 'dd') {
      const latLngText: string[] = input.split(',').map((latLng: string) => latLng.trim());
      lat = +latLngText[0];
      lng = +latLngText[1];
    } else if (this.latLngFormat === 'dms') {
      const latLng: string[] = input.split(/["N"S"E"W]+/);
      lat = convertDMStoDD(latLng[0].trim(), !input.includes('S'));
      lng = convertDMStoDD(latLng[1].trim(), !input.includes('W'));
    } else if (this.latLngFormat === 'dmm') {
      const latLng: string[] = input.split(',');
      lat = convertDMMtoDD(latLng[0].trim());
      lng = convertDMMtoDD(latLng[1].trim());
    }

    return new google.maps.LatLng(lat, lng);
  }

  private showNoLocErrorState(): void {
    this.element.parentElement.classList.add('error');
    this.noLocationFoundEl.classList.add('error');
  }

  private hideNoLocErrorState(): void {
    this.element.parentElement.classList.remove('error');
    this.noLocationFoundEl.classList.remove('error');
  }

  private showNoLocErrorDiv(): void {
    const inputBoundingRect: ClientRect = this.element.getBoundingClientRect();
    this.noLocationFoundEl.style.left = `${inputBoundingRect.left}px`;
    this.noLocationFoundEl.style.top = `${inputBoundingRect.bottom}px`;
    this.noLocationFoundEl.style.width = this.pacContainer.style.width ||
      `${inputBoundingRect.right - inputBoundingRect.left}px`
      ;
    this.noLocationFoundEl.style.display = 'block';
    this.element.addEventListener('keydown', this.onKeyDown.bind(this));
  }

  private hideNoLocErrorDiv(): void {
    this.noLocationFoundEl.style.display = 'none';
    this.element.removeEventListener('keydown', this.onKeyDown);
    this.hideNoLocErrorState();
  }

  private handleTabPress(input: HTMLInputElement): void {
    input.addEventListener('keydown', event => {
      if ((event.which === KEY_CODES.Tab || event.which === KEY_CODES.Enter)) {
        if (this.isLatLngEntered) {
          this.onLatLngEntered();
        } else {
          this.isPlaceSelecting = true;
          const suggestionSelected: boolean = document.getElementsByClassName('pac-item-selected').length > 0;
          if (!suggestionSelected) {
            const e: KeyboardEvent = createKeyboardEvent('keydown', {
              code: `Down`,
              key: `Down`,
              keyCode: KEY_CODES.Down,
            } as KeyboardEventInit);
            input.dispatchEvent(e);
          }
        }
      }
    });
  }

  private onLatLngEntered(): void {
    const latlng: google.maps.LatLng = this.convertTextToLatLng(this.element.value);
    this.geocoderService.geocode({ location: latlng }, (results, status) => {
      if (status === google.maps.GeocoderStatus.OK && results[0]) {
        this.onPlaceChange.emit(results[0]);
      } else if (this.isNotFoundErrorVisible) this.showNoLocErrorState();
      else this.showNoLocErrorDiv();
    });
  }

  private registerDropdownClick(): void {
    this.pacContainer.addEventListener('mousedown', () => {
      this.isPlaceSelecting = true;
      this.hideNoLocErrorDiv();
    });
  }

  private initMutationObserver(): void {
    this.mutationObserver = new MutationObserver((mutations: MutationRecord[]) => {
      for (const mutation of mutations) {
        if (mutation.type === 'childList') {
          this.hideNoLocErrorDiv();
          if (!this.pacContainer.hasChildNodes() && this.element.value) {
            if (this.isPlaceSelected || this.isLatLngEntered) this.hideNoLocErrorDiv();
            else if (!this.isPlaceSelecting) this.showNoLocErrorDiv();
          } else this.hideNoLocErrorDiv();
        }
      }
    });
  }

  private detectDropdownExistance(): void {
    const predicate = (mutation: MutationRecord) => {
      if (mutation.type == 'childList') {
        return Array.from(mutation.addedNodes).find((node: HTMLElement) => {
          return node.classList.contains('pac-container');
        }) as HTMLElement;
      }
    };
    observeElement(document.body, predicate, { childList: true })
      .then((element: HTMLElement) => {
        this.pacContainer = element;
        this.startObserving();
        this.registerDropdownClick();

        // set mutations on `pacContainer` so that we can display dropdown at right position
        observeElement(element, mutation => mutation.type == 'childList' ? element : null, { childList: true }).then(() => {
          this.setDropdownBounds();
        });
      });
  }
}
